// Primary/secondary reply language for mid-call language switching.
// Mirrors techladder-core app/domain/agents/language_preference.py: the runtime
// only tells scripts apart, so a pair is English plus the STT's native language.

export type ReplyLanguage = "english" | "hinglish" | "arabic";

export interface LanguagePreference {
  primaryLanguage: ReplyLanguage | null;
  secondaryLanguage: ReplyLanguage | null;
  languageSwitchPercent: number | null;
}

export const DEFAULT_SWITCH_PERCENT = 50;

export const REPLY_LANGUAGE_LABELS: Record<ReplyLanguage, string> = {
  english: "English",
  hinglish: "Hindi (Hinglish)",
  arabic: "Arabic",
};

export const EMPTY_LANGUAGE_PREFERENCE: LanguagePreference = {
  primaryLanguage: null,
  secondaryLanguage: null,
  languageSwitchPercent: null,
};

export function nativeLanguageForStt(sttLanguage: string | null | undefined): ReplyLanguage | null {
  const subtag = String(sttLanguage || "hi").trim().toLowerCase().replace("_", "-").split("-")[0];
  if (subtag === "ar") return "arabic";
  if (subtag === "hi" || subtag === "en") return "hinglish";
  return null;
}

// The two languages a version may use with this STT language, or [] when the
// runtime cannot switch for it.
export function replyLanguagesForStt(sttLanguage: string | null | undefined): ReplyLanguage[] {
  const native = nativeLanguageForStt(sttLanguage);
  return native ? [native, "english"] : [];
}

export function defaultLanguagePreference(sttLanguage: string | null | undefined): LanguagePreference {
  const native = nativeLanguageForStt(sttLanguage);
  if (!native) return EMPTY_LANGUAGE_PREFERENCE;
  return { primaryLanguage: native, secondaryLanguage: "english", languageSwitchPercent: DEFAULT_SWITCH_PERCENT };
}

export function readReplyLanguage(value: unknown): ReplyLanguage | null {
  return value === "english" || value === "hinglish" || value === "arabic" ? value : null;
}

// Null when nothing is set (the runtime keeps its fixed threshold); otherwise
// the reason the pair can't be saved.
export function languagePreferenceProblem(
  preference: LanguagePreference,
  sttLanguage: string | null | undefined,
): string | null {
  const { primaryLanguage, secondaryLanguage, languageSwitchPercent } = preference;
  if (!primaryLanguage && !secondaryLanguage) return null;
  if (!primaryLanguage || !secondaryLanguage) return "Choose both a primary and a secondary language.";
  const allowed = replyLanguagesForStt(sttLanguage);
  if (!allowed.length) return "Primary and secondary languages need Hindi, English or Arabic transcription.";
  const pair = new Set([primaryLanguage, secondaryLanguage]);
  if (pair.size !== 2 || !allowed.every((language) => pair.has(language))) {
    return `With this transcription language the pair must be ${allowed.map((l) => REPLY_LANGUAGE_LABELS[l]).join(" and ")}.`;
  }
  const percent = languageSwitchPercent ?? DEFAULT_SWITCH_PERCENT;
  if (!Number.isInteger(percent) || percent < 1 || percent > 99) return "Switch share must be a whole number from 1 to 99.";
  return null;
}

// The pair to keep after the STT language changes: unchanged if still valid,
// re-defaulted when switching is on, otherwise cleared.
export function preferenceAfterSttChange(
  preference: LanguagePreference,
  sttLanguage: string,
  languageSwitch: boolean,
): LanguagePreference {
  if (!languagePreferenceProblem(preference, sttLanguage)) return preference;
  return languageSwitch ? defaultLanguagePreference(sttLanguage) : EMPTY_LANGUAGE_PREFERENCE;
}
